import { useLocation } from 'react-router-dom';

import {
  BaseMenuDto,
  useEmployeeHeaderGet,
} from '@sbv-clients/data-access-api';

type Crumb = { label: string };

type BreadcrumbContext = {
  pathname: string;
  matchedMenuItem: BaseMenuDto | undefined;
  parentLabel: string | null | undefined;
  fullName: string | null | undefined;
  employeeId: number;
};

type BreadcrumbResolver = {
  matches: (ctx: BreadcrumbContext) => boolean;
  resolve: (ctx: BreadcrumbContext) => Crumb[];
};

const crumbs = (...labels: (string | null | undefined)[]): Crumb[] =>
  labels.filter(Boolean).map((label) => ({ label: label! }));

const ADD_ROUTE_REGEX = /^\/[^/]+\/add$/;
const EDIT_ROUTE_REGEX = /^\/[^/]+\/[^/]+\/edit$/;

const resolvers: BreadcrumbResolver[] = [
  {
    matches: ({ pathname }) => pathname === '/' || pathname === '',
    resolve: () => [],
  },
  {
    matches: ({ pathname }) =>
      pathname.startsWith('/tools/') && pathname.split('/').length >= 3,
    resolve: ({ pathname }) => {
      const segment = pathname.split('/')[2];
      const TOOLS_SEGMENT_LABEL_MAP: Record<string, string> = {
        processing: 'Processing',
        'close-pay-period': 'Close Pay Period',
        'clock-communication': 'Clock Communication',
        imports: 'Import',
      };
      return crumbs('Tools', TOOLS_SEGMENT_LABEL_MAP[segment] ?? segment);
    },
  },
  {
    matches: ({ pathname }) => pathname.startsWith('/contact-us'),
    resolve: ({ parentLabel }) => crumbs(parentLabel, 'Contact Us'),
  },
  {
    matches: ({ pathname }) => pathname.startsWith('/preferences'),
    resolve: () => crumbs('Preferences'),
  },
  {
    matches: ({ pathname }) => pathname.endsWith('/employees'),
    resolve: ({ parentLabel, matchedMenuItem }) =>
      crumbs(parentLabel, matchedMenuItem?.label),
  },
  {
    matches: ({ pathname }) => /^\/employees\/\d+/.test(pathname),
    resolve: ({ parentLabel, matchedMenuItem, fullName, employeeId }) =>
      crumbs(
        parentLabel,
        matchedMenuItem?.label,
        fullName || String(employeeId)
      ),
  },
  {
    matches: () => true,
    resolve: ({ pathname, parentLabel, matchedMenuItem }) => {
      const labels = crumbs(parentLabel, matchedMenuItem?.label);
      if (ADD_ROUTE_REGEX.test(pathname)) labels.push({ label: 'Create' });
      if (EDIT_ROUTE_REGEX.test(pathname)) labels.push({ label: 'Edit' });
      return labels;
    },
  },
];

const extractEmployeeFromPathName = () => {
  const match = window.location.pathname.match(/employees\/(\d+)/);
  return match ? +match[1] : 0;
};

export const useBreadcrumbLabelsBuilder = (
  facilityMenuItems: BaseMenuDto[]
) => {
  const { pathname } = useLocation();
  const pathNamePrefix = `/${pathname.split('/')[1]}`;
  const employeeId = extractEmployeeFromPathName();
  const { data } = useEmployeeHeaderGet({ pkEmployeeId: employeeId });

  const matchedMenuItem = facilityMenuItems.find((item) =>
    item?.link?.includes(pathNamePrefix)
  );
  const parentLabel = facilityMenuItems.find(
    (item) => item.id === matchedMenuItem?.parentId
  )?.label;

  const ctx: BreadcrumbContext = {
    pathname,
    matchedMenuItem,
    parentLabel,
    fullName: data?.fullName,
    employeeId,
  };

  const resolver = resolvers.find((r) => r.matches(ctx));
  const breadCrumbLabels = (resolver?.resolve(ctx) ?? []).filter((item) =>
    Boolean(item.label)
  );

  return { breadCrumbLabels };
};
